import { useEffect, useState } from "react"
import { Feature } from "@prisma/client"
import { toast } from "react-toastify"
import { ClientTypeDto } from "@dialogics/shared/dto/types"
import { useGetFeaturesQuery } from "../feature/service"
import { useBulkPatchWeightsMutation, useGetWeightsByOrganizationGroupQuery } from "./feature-weight-service"

type MatrixCell = { id?: number; weight: string }
type MatrixRow = { feature: Feature; cells: Record<number, MatrixCell> }

type FeatureWeightMatrixProps = {
  organizationGroupId: number
  callTypes: ClientTypeDto[]
}

export const FeatureWeightMatrix = ({ organizationGroupId, callTypes }: FeatureWeightMatrixProps) => {
  const { data: weights, isLoading } = useGetWeightsByOrganizationGroupQuery({ organizationGroupId })
  const { data: allFeatures } = useGetFeaturesQuery()
  const [bulkPatchWeights, { isLoading: isSaving }] = useBulkPatchWeightsMutation()

  const [rows, setRows] = useState<MatrixRow[]>([])
  const [deleteIds, setDeleteIds] = useState<number[]>([])
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>("")

  useEffect(() => {
    if (!weights) return

    const rowMap = new Map<number, MatrixRow>()

    for (const w of weights) {
      if (!rowMap.has(w.feature_id)) {
        rowMap.set(w.feature_id, { feature: w.feature, cells: {} })
      }
      rowMap.get(w.feature_id)!.cells[w.client_type_id] = {
        id: w.id,
        weight: String(w.weight),
      }
    }

    const sorted = [...rowMap.values()].sort((a, b) => a.feature.code.localeCompare(b.feature.code))
    setRows(sorted)
    setDeleteIds([])
  }, [weights])

  const usedFeatureIds = new Set(rows.map(r => r.feature.id))
  const availableFeatures = (allFeatures ?? []).filter(f => !usedFeatureIds.has(f.id))

  const handleAddRow = () => {
    const id = Number(selectedFeatureId)
    if (!id) return
    const feature = (allFeatures ?? []).find(f => f.id === id)
    if (!feature) return

    setRows(prev => [...prev, { feature, cells: {} }].sort((a, b) => a.feature.code.localeCompare(b.feature.code)))
    setSelectedFeatureId("")
  }

  const handleDeleteRow = (featureId: number) => {
    const row = rows.find(r => r.feature.id === featureId)
    if (!row) return
    const idsToDelete = Object.values(row.cells)
      .filter(c => c.id !== undefined)
      .map(c => c.id!)
    setDeleteIds(prev => [...prev, ...idsToDelete])
    setRows(prev => prev.filter(r => r.feature.id !== featureId))
  }

  const handleCellChange = (featureId: number, clientTypeId: number, value: string) => {
    setRows(prev =>
      prev.map(row => {
        if (row.feature.id !== featureId) return row
        return {
          ...row,
          cells: {
            ...row.cells,
            [clientTypeId]: { ...row.cells[clientTypeId], weight: value },
          },
        }
      }),
    )
  }

  const handleSave = async () => {
    const upsert = rows.flatMap(row =>
      callTypes
        .filter(ct => row.cells[ct.id]?.weight !== "" && row.cells[ct.id]?.weight !== undefined)
        .map(ct => ({
          featureId: row.feature.id,
          clientTypeId: ct.id,
          weight: Number(row.cells[ct.id].weight),
        })),
    )
    try {
      await bulkPatchWeights({ upsert, deleteIds }).unwrap()
      toast.success("Веса признаков сохранены")
      setDeleteIds([])
    } catch {
      toast.error("Ошибка при сохранении весов")
    }
  }

  if (isLoading) return <div>Загрузка весов...</div>

  return (
    <div>
      <div className="flex items-end gap-2 mb-4">
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Добавить признак</legend>
          <select
            className="select select-bordered"
            value={selectedFeatureId}
            onChange={e => setSelectedFeatureId(e.target.value)}
          >
            <option value="">— выберите признак —</option>
            {availableFeatures.map(f => (
              <option key={f.id} value={f.id}>
                {f.code} — {f.title}
              </option>
            ))}
          </select>
        </fieldset>
        <button className="btn btn-secondary" onClick={handleAddRow} disabled={!selectedFeatureId}>
          Добавить
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-base-content/60">Нет признаков. Добавьте строку выше.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-sm border border-base-300">
            <thead>
              <tr>
                <th className="bg-base-200 sticky left-0 z-10">Признак</th>
                {callTypes.map(ct => (
                  <th key={ct.id} className="bg-base-200 text-center max-w-37.5 whitespace-normal wrap-break-word">
                    {ct.title}
                  </th>
                ))}
                <th className="bg-base-200"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.feature.id}>
                  <td className="sticky left-0 bg-base-100 font-mono text-sm whitespace-nowrap">
                    <span className="font-semibold">{row.feature.code}</span>
                    <br />
                    <span className="text-xs text-base-content/60">{row.feature.title}</span>
                  </td>
                  {callTypes.map(ct => (
                    <td key={ct.id} className="text-center">
                      <input
                        type="number"
                        step="any"
                        className="input input-sm w-24"
                        value={row.cells[ct.id]?.weight ?? ""}
                        onChange={e => handleCellChange(row.feature.id, ct.id, e.target.value)}
                      />
                    </td>
                  ))}
                  <td>
                    <button className="btn btn-sm btn-error" onClick={() => handleDeleteRow(row.feature.id)}>
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Сохранение..." : "Сохранить веса"}
        </button>
      </div>
    </div>
  )
}
